import { Box, Card, CardContent, Stack } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import CodeSnippetView from './CodeSnippetView';
import type { CodeSnippetCodeByDropZone, CodeSnippetDropZoneMode, CodeSnippetImport } from '../types';

interface CodeEmbedProps {
  code: string;
  insertText?: string;
  title: string;
  type: string | undefined;
  dropZone?: CodeSnippetDropZoneMode;
  codeByDropZone?: CodeSnippetCodeByDropZone;
  imports?: CodeSnippetImport[];
  frameworkClassName?: string;
  display?: string;
  receiverClassName?: string;
  disabled?: boolean;
}

const CodeEmbed = ({
  code: codeWithImports,
  insertText,
  title,
  type,
  dropZone,
  codeByDropZone,
  imports,
  frameworkClassName,
  display,
  receiverClassName,
  disabled = false,
}: CodeEmbedProps) => {
  const concise = useMemo(
    () =>
      codeWithImports
        .split('\n')
        .filter((line) => line !== 'from sphero import *')
        // Collapse repeated blank lines to save space. Two blank lines after imports
        // is conventional but a big waste of space here.
        .filter((line, index, array) => index === 0 || !(line.length === 0 && array[index - 1].length === 0))
        .join('\n')
        .trim(),
    [codeWithImports]
  );

  const { attributes, listeners, setNodeRef } = useDraggable({
    id: title,
    data: { code: concise, insertText, type, dropZone, codeByDropZone, imports, frameworkClassName, receiverClassName },
    disabled,
  });
  const dragAttributes = disabled ? {} : attributes;
  const dragListeners = disabled ? {} : listeners;

  return (
    <Card
      aria-disabled={disabled ? true : undefined}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 1,
        borderColor: disabled ? 'action.disabled' : 'divider',
        borderStyle: disabled ? 'dashed' : 'solid',
        m: 0,
        p: 0,
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          ...(disabled
            ? {}
            : {
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }),
        }}
      >
        <CardContent
          sx={{
            p: 0,
            '&:last-child': { pb: 0 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <div style={{ width: '100%' }} ref={setNodeRef} {...dragAttributes} {...dragListeners}>
            <Stack
              direction="row"
              alignItems={'center'}
              width={'100%'}
              minWidth={0}
              sx={{ cursor: disabled ? 'not-allowed' : 'grab' }}
              justifyContent={'flex-start'}
            >
              <Box display={'flex'} flexShrink={0}>
                <DragIndicatorIcon color={disabled ? 'disabled' : 'inherit'} />
              </Box>

              <CodeSnippetView title={title!} value={display !== undefined ? display : concise} />
            </Stack>
          </div>
        </CardContent>
      </Box>
    </Card>
  );
};

export default CodeEmbed;
